/**
 * Communication Template Generator Component - SOP #1
 * Generates and displays communication templates for copy-paste
 */

import React, { useState } from "react";
import {
  Modal,
  Select,
  Button,
  Input,
  Space,
  Typography,
  message,
  Spin,
  Card,
} from "antd";
import {
  MessageOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import {
  generateLeadTemplate,
  generateQuoteTemplate,
  copyToClipboard,
} from "../services/communicationTemplateService";
import api from "../../../shared/api.jsx";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

/**
 * Communication Template Generator
 * @param {Object} props - Component props
 * @param {string} props.leadId - Lead ID (for lead templates)
 * @param {string} props.quoteId - Quote ID (for quote templates)
 * @param {boolean} props.visible - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 */
const CommunicationTemplateGenerator = ({
  leadId,
  quoteId,
  visible,
  onClose,
}) => {
  const [templateType, setTemplateType] = useState(null);
  const [template, setTemplate] = useState("");
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const leadTemplateTypes = [
    { value: "welcome", label: "Welcome Message" },
    { value: "photo_request", label: "Request Photos" },
    { value: "pre_quote_confirmation", label: "Pre-Quote Confirmation" },
  ];

  const quoteTemplateTypes = [
    { value: "quote_ready", label: "Quote Ready (WhatsApp)" },
    { value: "email_template", label: "Email Template" },
  ];

  const templateTypes = leadId ? leadTemplateTypes : quoteTemplateTypes;

  const handleTemplateTypeChange = async (value) => {
    setTemplateType(value);
    setTemplate("");
    setEmailData(null);
    setCopied(false);

    if (!value) return;

    setLoading(true);
    try {
      let generatedTemplate;
      if (leadId) {
        generatedTemplate = await generateLeadTemplate(leadId, value);
      } else if (quoteId) {
        // For email templates, we need to get the structured response
        if (value === "email_template") {
          const response = await api.get(
            `/templates/quote/${quoteId}/${value}`
          );
          generatedTemplate = response.data.template;
          // Store structured email data if available
          if (response.data.email) {
            setEmailData(response.data.email);
          }
        } else {
          generatedTemplate = await generateQuoteTemplate(quoteId, value);
        }
      }

      setTemplate(generatedTemplate || "");
    } catch (error) {
      console.error("Error generating template:", error);
      message.error("Failed to generate template");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!template) return;

    const success = await copyToClipboard(template);
    if (success) {
      setCopied(true);
      message.success("Template copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    setTemplateType(null);
    setTemplate("");
    setEmailData(null);
    setCopied(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <span>Communication Template Generator</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
        <Button
          key="copy"
          type="primary"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopy}
          disabled={!template}
        >
          {copied ? "Copied!" : "Copy to Clipboard"}
        </Button>,
      ]}
      width={800}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Text strong>Select Template Type: </Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose a template..."
            value={templateType}
            onChange={handleTemplateTypeChange}
          >
            {templateTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Generating template...</Text>
            </div>
          </div>
        )}

        {template && !loading && (
          <Card>
            <Title level={5}>Template Preview</Title>
            {/* Show subject separately for email templates */}
            {emailData && emailData.subject && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Subject: </Text>
                <Text>{emailData.subject}</Text>
              </div>
            )}
            <TextArea
              value={template}
              readOnly
              rows={emailData ? 15 : 12}
              style={{ fontFamily: "monospace", fontSize: "13px" }}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Click &quot;Copy to Clipboard&quot; to copy this template, then
                paste it into WhatsApp or Email.
              </Text>
            </div>
          </Card>
        )}

        {!templateType && !loading && (
          <Card>
            <Text type="secondary">
              Select a template type above to generate a communication template.
            </Text>
          </Card>
        )}
      </Space>
    </Modal>
  );
};

export default CommunicationTemplateGenerator;
